import { CSRF_HEADER_NAME, readCsrfToken, rememberCsrfToken } from "./csrf";
import type { PortalApiClient, StaffFolder } from "./PortalApiClient";
import type {
  AllowlistRequest,
  ApiErrorCode,
  ChangePasswordRequest,
  CreateSharingRequest,
  DeleteFileRequest,
  DeliveredBrowseResponse,
  FilesResponse,
  FolderRequest,
  ListFilesParams,
  LoginRequest,
  LogoutResponse,
  Me,
  MemberCredential,
  OutboundBrowseResponse,
  RejectReason,
  Sharing,
  SharingDetail,
  UploadController,
  UploadProgress,
  UploadResult,
} from "./types";
import { ApiError, UploadCancelledError } from "./types";

/** Every route lives under this prefix (the `servers` block in docs/api/portal-api.yaml). */
const API_BASE = "/api/portal";

const KNOWN_ERROR_CODES: ReadonlySet<ApiErrorCode> = new Set([
  "invalid_request",
  "invalid_credentials",
  "password_change_required",
  "not_staff",
  "local_staff_auth_disabled",
  "sftpgo_unavailable",
  "internal_error",
  "forbidden",
  "session_expired",
  "sharing_not_found",
  "invalid_display_name",
  "code_generation_failed",
  "invalid_allowlist",
  "invalid_member_id",
  "id_collision",
  "id_generation_failed",
  "member_not_found",
  "invalid_folder",
  "invalid_path",
  "folder_exists",
  "folder_limit_reached",
  "folder_not_found",
  "folder_not_empty",
  "folder_busy",
  "invalid_file_name",
  "file_too_large",
  "upload_rejected",
  "scan_busy",
  "scan_unavailable",
  "storage_unavailable",
  "download_failed",
  "password_rejected",
  "cross_site_request",
  "csrf_invalid",
  "conflict",
  "too_many_transfers",
  "too_many_uploads",
  "too_many_folder_operations",
  "upstream_error",
  "not_found",
  "unknown",
]);

/** Falls back to "unknown" for any code the BFF sends that this client does not recognise yet. */
function toApiErrorCode(code: string | undefined): ApiErrorCode {
  return code !== undefined && KNOWN_ERROR_CODES.has(code as ApiErrorCode)
    ? (code as ApiErrorCode)
    : "unknown";
}

/**
 * The BFF's `message` field is not always present (docs/api/portal-api.yaml
 * marks it optional on the Error schema), so this client owns the default
 * copy shown to the user, keyed by code. A `message` field in the
 * response, when present, still wins (it sometimes carries SFTPGo's own
 * rejection text verbatim, for example on password_rejected).
 * `too_many_transfers`, `too_many_uploads`, `too_many_folder_operations`,
 * `scan_busy`, `scan_unavailable` and `storage_unavailable` are built
 * dynamically instead, from the response's Retry-After header (see
 * `messageForRetryAfter`); `upload_rejected` is built from its `reason`
 * (see `REJECT_DEFAULT_MESSAGES`). None of the six have an entry here.
 */
const DEFAULT_MESSAGES: Record<Exclude<ApiErrorCode, RetryAfterCode | "upload_rejected">, string> = {
  invalid_request: "That request was not valid.",
  invalid_credentials: "Invalid ID or password.",
  password_change_required: "You must change your password before continuing.",
  not_staff: "This account is not a member of the staff group.",
  local_staff_auth_disabled: "Local staff sign-in is disabled.",
  sftpgo_unavailable: "The file transfer backend is not reachable. Please try again.",
  internal_error: "Something went wrong. Please try again.",
  forbidden: "You do not have access to this page.",
  session_expired: "Your session has expired. Please sign in again.",
  sharing_not_found: "No sharing with this code.",
  invalid_display_name: "Enter a partner name between 1 and 64 characters.",
  code_generation_failed: "Could not generate a unique sharing code. Please try again.",
  invalid_allowlist: "Check the allowlist: one CIDR per entry, no /0 prefixes.",
  invalid_member_id: "That member does not belong to this sharing.",
  id_collision: "Could not generate a unique member ID, please retry.",
  id_generation_failed: "Could not generate a unique member ID.",
  member_not_found: "No such member.",
  invalid_folder: "Unknown folder.",
  invalid_path: "That folder path is not valid.",
  folder_exists: "A file or folder with that name already exists here.",
  folder_limit_reached: "This folder already has the maximum number of subfolders.",
  folder_not_found: "That folder no longer exists.",
  folder_not_empty: "Only empty folders can be deleted. This folder still contains files.",
  folder_busy: "This folder is being used by an upload in progress. Try again when it finishes.",
  invalid_file_name: "That file name is not valid.",
  file_too_large: "That file exceeds the maximum allowed size.",
  download_failed: "The file was not found or is not accessible.",
  password_rejected: "That password was rejected.",
  cross_site_request: "This request was rejected as cross-site.",
  csrf_invalid: "Your session expired. Please try again.",
  conflict: "This conflicts with another change. Please refresh and try again.",
  upstream_error: "The upstream service is unavailable. Please try again.",
  not_found: "The requested resource was not found.",
  unknown: "Something went wrong. Please try again.",
};

/**
 * docs/api/portal-api.yaml's shared TooManyTransfers and ScanUnavailable
 * responses cover six distinct causes (M1, a security-review follow-up,
 * ADR 0010 and ADR 0011), each with its own wording so the message
 * actually explains what to do:
 * - `too_many_transfers`: the per-subject or global concurrent-transfer
 *   cap is already reached (other transfers are still in flight);
 * - `too_many_uploads`: the per-subject upload *rate* limit
 *   (PORTAL_MAX_UPLOADS_PER_MINUTE, partner and staff uploads) was exceeded;
 * - `too_many_folder_operations`: the per-subject folder create/delete
 *   rate limit (FOLDER_OPS_PER_MINUTE, ADR 0010 section 7) was exceeded;
 * - `scan_busy`: no scanner receiving slot was free (ADR 0011 section 7);
 * - `scan_unavailable`: the scanner or the malware engine failed;
 * - `storage_unavailable`: the file passed the scan but could not be
 *   stored.
 * All six carry the retry delay in the Retry-After header (seconds), which
 * the message embeds when present; the server always sends its own
 * `message` too (docs/api/portal-api.yaml's ScanUnavailable response), so
 * this is mostly a defensive fallback.
 */
type RetryAfterCode =
  | "too_many_transfers"
  | "too_many_uploads"
  | "too_many_folder_operations"
  | "scan_busy"
  | "scan_unavailable"
  | "storage_unavailable";

const RETRY_AFTER_MESSAGE_TEMPLATES: Record<RetryAfterCode, (seconds: string) => string> = {
  too_many_transfers: (seconds) =>
    `You already have several transfers in progress. Please wait ${seconds} and try again.`,
  too_many_uploads: (seconds) => `Too many uploads in a short time. Please wait ${seconds} and try again.`,
  too_many_folder_operations: (seconds) =>
    `Too many folder changes in a short time. Please wait ${seconds} and try again.`,
  scan_busy: (seconds) => `The malware scan is at capacity right now. Nothing was stored. Try again in ${seconds}.`,
  scan_unavailable: (seconds) =>
    `The malware scan is temporarily unavailable. Nothing was stored. Try again in ${seconds}.`,
  storage_unavailable: (seconds) =>
    `Your file passed the malware scan but could not be stored. Try again in ${seconds}.`,
};

const DEFAULT_RETRY_AFTER_MESSAGES: Record<RetryAfterCode, string> = {
  too_many_transfers: "You already have several transfers in progress. Please try again shortly.",
  too_many_uploads: "Too many uploads in a short time. Please try again shortly.",
  too_many_folder_operations: "Too many folder changes in a short time. Please try again shortly.",
  scan_busy: "The malware scan is at capacity right now. Nothing was stored. Try again in a few minutes.",
  scan_unavailable: "The malware scan is temporarily unavailable. Nothing was stored. Try again in a few minutes.",
  storage_unavailable: "Your file passed the malware scan but could not be stored. Try again in a few minutes.",
};

function messageForRetryAfter(code: RetryAfterCode, retryAfter: string | null): string {
  const seconds = retryAfter !== null ? Number.parseInt(retryAfter, 10) : NaN;
  if (Number.isNaN(seconds) || seconds < 0) {
    return DEFAULT_RETRY_AFTER_MESSAGES[code];
  }
  return RETRY_AFTER_MESSAGE_TEMPLATES[code](`${seconds} second${seconds === 1 ? "" : "s"}`);
}

function isRetryAfterCode(code: ApiErrorCode): code is RetryAfterCode {
  return (
    code === "too_many_transfers" ||
    code === "too_many_uploads" ||
    code === "too_many_folder_operations" ||
    code === "scan_busy" ||
    code === "scan_unavailable" ||
    code === "storage_unavailable"
  );
}

/**
 * ADR 0011 section 5: the fixed, server-side message per rejection reason
 * (`internal/portal/handlers_files.go`'s `rejectMessage`), used only when
 * the response is somehow missing its own `message` (the contract says it
 * is always present).
 */
const REJECT_DEFAULT_MESSAGES: Record<RejectReason, string> = {
  malware: "This file was not uploaded: the security check detected malware. Nothing was stored.",
  unscannable:
    "This file was not uploaded: it could not be checked for malware (for example an encrypted or password-protected archive). Nothing was stored.",
  timeout: "This file was not uploaded: the security check timed out. Nothing was stored.",
  // too_large never actually reaches a 422 (it is a 413, checked before the
  // scan runs), but the type is shared with internal reporting.
  too_large: "That file exceeds the maximum allowed size.",
};

const REJECT_REASONS: ReadonlySet<string> = new Set(["malware", "unscannable", "too_large", "timeout"]);

function toRejectReason(value: unknown): RejectReason | undefined {
  return typeof value === "string" && REJECT_REASONS.has(value) ? (value as RejectReason) : undefined;
}

function defaultMessageFor(code: Exclude<ApiErrorCode, "upload_rejected">, retryAfter: string | null): string {
  return isRetryAfterCode(code) ? messageForRetryAfter(code, retryAfter) : DEFAULT_MESSAGES[code];
}

/**
 * Central redirects. Any authenticated call that comes back 401 (always
 * `session_expired` on this contract) sends the user back to /login. Any
 * call that comes back 403 `password_change_required` sends them to
 * /change-password: ADR 0008 section 2 says this can happen on any
 * partner route, not only /session/login (the login routes themselves are
 * exempt, see `redirectOnPasswordChangeRequired`: LoginPage handles that
 * case itself, without a full-page navigation).
 */
function redirectTo(path: string): void {
  if (window.location.pathname !== path) {
    window.location.assign(path);
  }
}

/**
 * A 403 `forbidden` can mean "wrong session kind for this route" (stay put,
 * RequireRole already guards against that) or "the session was expired or
 * revoked server-side since the last page load" (a staff session's IdP
 * token, or a partner's SFTPGo JWT, can both go invalid between requests).
 * Only the second case should force a redirect, so this re-reads
 * /session/me (which never itself fails) and checks its kind. Best-effort:
 * network failures here are swallowed, since the original 403 is already
 * being thrown to the caller regardless.
 */
async function redirectToLoginIfSessionNowAnon(): Promise<void> {
  try {
    const response = await fetch(`${API_BASE}/session/me`, {
      method: "GET",
      credentials: "same-origin",
    });
    const me = (await response.json()) as Me;
    if (me.kind === "anon") {
      redirectTo("/login");
    }
  } catch {
    // Best-effort only; the caller already has the original error to throw.
  }
}

interface ErrorPayload {
  error?: string;
  message?: string;
  /** Only on a 422 upload_rejected body (ADR 0011 section 5). */
  reason?: string;
  /** Only on a 422 upload_rejected body. */
  reference?: string;
}

/** Builds a query string from optional values, dropping any that are absent or empty (ADR 0010: an empty `path` means the root, so it is never sent). */
function query(params: Record<string, string | undefined>): string {
  const entries = Object.entries(params).filter(([, value]) => value !== undefined && value !== "");
  if (entries.length === 0) {
    return "";
  }
  return `?${entries.map(([key, value]) => `${key}=${encodeURIComponent(value!)}`).join("&")}`;
}

async function safeJson(response: Response): Promise<ErrorPayload | undefined> {
  try {
    return (await response.json()) as ErrorPayload;
  } catch {
    return undefined;
  }
}

/**
 * Builds the `ApiError` thrown for a non-ok response, shared by the plain
 * fetch-based routes and `uploadFile`'s XMLHttpRequest path below: a 422
 * `upload_rejected` carries its `reason` and `reference` (ADR 0011 section
 * 5); every other code falls back to `defaultMessageFor` when the response
 * itself carries no `message`.
 */
function buildApiError(status: number, payload: ErrorPayload | undefined, retryAfter: string | null): ApiError {
  const code = toApiErrorCode(payload?.error);
  if (code === "upload_rejected") {
    const reason = toRejectReason(payload?.reason);
    const message = payload?.message ?? (reason ? REJECT_DEFAULT_MESSAGES[reason] : DEFAULT_MESSAGES.unknown);
    return new ApiError(status, code, message, { reason, reference: payload?.reference });
  }
  return new ApiError(status, code, payload?.message ?? defaultMessageFor(code, retryAfter));
}

async function toApiError(response: Response, payload?: ErrorPayload): Promise<ApiError> {
  const body = payload ?? (await safeJson(response));
  return buildApiError(response.status, body, response.headers.get("Retry-After"));
}

interface RequestOptions {
  method: "GET" | "POST";
  body?: unknown;
  /** False for /session/login and /session/login/staff: a 401 there is a login failure, not a lost session. */
  redirectOnUnauthorized?: boolean;
  /** False for /session/login and /session/login/staff: LoginPage handles that 403 itself (an SPA-internal navigation, not a full-page one). */
  redirectOnPasswordChangeRequired?: boolean;
}

async function request<T>(path: string, options: RequestOptions): Promise<T> {
  const {
    method,
    body,
    redirectOnUnauthorized = true,
    redirectOnPasswordChangeRequired = true,
  } = options;
  const headers: Record<string, string> = {};
  if (body !== undefined) {
    headers["Content-Type"] = "application/json";
  }
  if (method !== "GET") {
    const csrfToken = readCsrfToken();
    if (csrfToken) {
      headers[CSRF_HEADER_NAME] = csrfToken;
    }
  }

  const response = await fetch(`${API_BASE}${path}`, {
    method,
    headers,
    credentials: "same-origin",
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  if (response.status === 401 && redirectOnUnauthorized) {
    redirectTo("/login");
    throw await toApiError(response);
  }

  if (response.status === 403) {
    const payload = await safeJson(response);
    if (payload?.error === "password_change_required") {
      if (redirectOnPasswordChangeRequired) {
        redirectTo("/change-password");
      }
    } else if (payload?.error === "forbidden") {
      await redirectToLoginIfSessionNowAnon();
    }
    throw await toApiError(response, payload);
  }

  if (!response.ok) {
    throw await toApiError(response);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return (await response.json()) as T;
}

function rememberMeCsrf(me: Me): Me {
  rememberCsrfToken(me.csrfToken);
  return me;
}

/**
 * Streams a file to `path` as `multipart/form-data` with XMLHttpRequest,
 * the one way to get upload progress events in a browser (ADR 0008
 * section 1). Cancelling calls `xhr.abort()`, which rejects the returned
 * promise with UploadCancelledError.
 *
 * ADR 0011: the upload now waits for the scanner's verdict, so this
 * reports two phases through `onProgress`: "uploading" while the body is
 * being sent (`xhr.upload.onprogress`), then "checking" once
 * `xhr.upload.onload` fires and the response is still pending (the scan
 * and, on a clean verdict, the store - about 1 to 2 minutes for a 2 GiB
 * file). There is no further progress to report during "checking": the
 * caller shows an indeterminate bar until `xhr.onload` settles the
 * promise.
 */
function uploadFile(
  path: string,
  file: File,
  onProgress?: (progress: UploadProgress) => void,
): UploadController {
  const xhr = new XMLHttpRequest();
  const formData = new FormData();
  formData.append("file", file);

  const result = new Promise<UploadResult>((resolve, reject) => {
    xhr.open("POST", `${API_BASE}${path}`);
    const csrfToken = readCsrfToken();
    if (csrfToken) {
      xhr.setRequestHeader(CSRF_HEADER_NAME, csrfToken);
    }

    xhr.upload.onprogress = (event) => {
      onProgress?.({ phase: "uploading", loaded: event.loaded, total: event.lengthComputable ? event.total : null });
    };
    xhr.upload.onload = () => {
      onProgress?.({ phase: "checking", loaded: file.size, total: file.size });
    };

    xhr.onabort = () => reject(new UploadCancelledError());
    xhr.onerror = () =>
      reject(new ApiError(0, "sftpgo_unavailable", DEFAULT_MESSAGES.sftpgo_unavailable));

    xhr.onload = () => {
      if (xhr.status === 401) {
        redirectTo("/login");
      }
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          resolve(JSON.parse(xhr.responseText) as UploadResult);
          return;
        } catch {
          reject(new ApiError(xhr.status, "unknown", DEFAULT_MESSAGES.unknown));
          return;
        }
      }
      let payload: ErrorPayload | undefined;
      try {
        payload = JSON.parse(xhr.responseText) as ErrorPayload;
      } catch {
        payload = undefined;
      }
      if (xhr.status === 403) {
        if (payload?.error === "password_change_required") {
          redirectTo("/change-password");
        } else if (payload?.error === "forbidden") {
          void redirectToLoginIfSessionNowAnon();
        }
      }
      reject(buildApiError(xhr.status, payload, xhr.getResponseHeader("Retry-After")));
    };

    xhr.send(formData);
  });

  return { result, cancel: () => xhr.abort() };
}

export function createHttpApiClient(): PortalApiClient {
  return {
    async login(loginRequest: LoginRequest) {
      const me = await request<Me>("/session/login", {
        method: "POST",
        body: loginRequest,
        redirectOnUnauthorized: false,
        redirectOnPasswordChangeRequired: false,
      });
      return rememberMeCsrf(me);
    },

    async loginStaff(loginRequest: LoginRequest) {
      const me = await request<Me>("/session/login/staff", {
        method: "POST",
        body: loginRequest,
        redirectOnUnauthorized: false,
        redirectOnPasswordChangeRequired: false,
      });
      return rememberMeCsrf(me);
    },

    logout() {
      return request<LogoutResponse>("/session/logout", { method: "POST" });
    },

    async me() {
      // GET /session/me is x-anonymous: it never 401s (returns kind
      // "anon" instead), so redirectOnUnauthorized is moot here but kept
      // at its default for consistency with every other GET.
      const me = await request<Me>("/session/me", { method: "GET" });
      return rememberMeCsrf(me);
    },

    changePassword(changePasswordRequest: ChangePasswordRequest) {
      return request<void>("/password/change", {
        method: "POST",
        body: changePasswordRequest,
      });
    },

    listFiles(params: ListFilesParams = {}) {
      return request<FilesResponse>(`/files${query({ folder: params.folder, path: params.path })}`, {
        method: "GET",
      });
    },

    uploadPartnerFile(file, path, onProgress) {
      return uploadFile(`/upload/partner${query({ path })}`, file, onProgress);
    },

    partnerDownloadUrl(name: string, path?: string) {
      return `${API_BASE}/download/partner/${encodeURIComponent(name)}${query({ path })}`;
    },

    partnerDeliveredDownloadUrl(name: string, path?: string) {
      return `${API_BASE}/download/partner/delivered/${encodeURIComponent(name)}${query({ path })}`;
    },

    createFolder(path: string) {
      const folderRequest: FolderRequest = { path };
      return request<{ path: string }>("/folders", { method: "POST", body: folderRequest });
    },

    async deleteFolder(path: string) {
      const folderRequest: FolderRequest = { path };
      await request<void>("/folders/delete", { method: "POST", body: folderRequest });
    },

    listSharings() {
      return request<Sharing[]>("/sharings", { method: "GET" });
    },

    createSharing(createSharingRequest: CreateSharingRequest) {
      return request<Sharing>("/sharings", { method: "POST", body: createSharingRequest });
    },

    getSharing(code: string) {
      return request<SharingDetail>(`/sharings/${encodeURIComponent(code)}`, { method: "GET" });
    },

    getSharingDelivered(code: string, path?: string) {
      return request<DeliveredBrowseResponse>(
        `/sharings/${encodeURIComponent(code)}/delivered${query({ path })}`,
        { method: "GET" },
      );
    },

    getSharingOutbound(code: string, path?: string) {
      return request<OutboundBrowseResponse>(
        `/sharings/${encodeURIComponent(code)}/outbound${query({ path })}`,
        { method: "GET" },
      );
    },

    createOutboundFolder(code: string, path: string) {
      const folderRequest: FolderRequest = { path };
      return request<{ path: string }>(`/sharings/${encodeURIComponent(code)}/outbound/folders`, {
        method: "POST",
        body: folderRequest,
      });
    },

    async deleteOutboundFolder(code: string, path: string) {
      const folderRequest: FolderRequest = { path };
      await request<void>(`/sharings/${encodeURIComponent(code)}/outbound/folders/delete`, {
        method: "POST",
        body: folderRequest,
      });
    },

    addMember(code: string) {
      return request<MemberCredential>(`/sharings/${encodeURIComponent(code)}/members`, {
        method: "POST",
      });
    },

    removeMember(code: string, id: string) {
      return request<void>(
        `/sharings/${encodeURIComponent(code)}/members/${encodeURIComponent(id)}/remove`,
        { method: "POST" },
      );
    },

    resetMemberPassword(code: string, id: string) {
      return request<MemberCredential>(
        `/sharings/${encodeURIComponent(code)}/members/${encodeURIComponent(id)}/reset-password`,
        { method: "POST" },
      );
    },

    setAllowlist(code: string, allowlistRequest: AllowlistRequest) {
      return request<Sharing>(`/sharings/${encodeURIComponent(code)}/allowlist`, {
        method: "POST",
        body: allowlistRequest,
      });
    },

    closeSharing(code: string) {
      return request<Sharing>(`/sharings/${encodeURIComponent(code)}/close`, { method: "POST" });
    },

    staffDownloadUrl(code: string, folder: StaffFolder, name: string, path?: string) {
      return `${API_BASE}/sharings/${encodeURIComponent(code)}/${folder}/download${query({ name, path })}`;
    },

    uploadStaffFile(code, file, path, onProgress) {
      return uploadFile(`/upload/staff/${encodeURIComponent(code)}${query({ path })}`, file, onProgress);
    },

    deleteOutboundFile(code: string, deleteFileRequest: DeleteFileRequest) {
      return request<void>(`/sharings/${encodeURIComponent(code)}/outbound/delete`, {
        method: "POST",
        body: deleteFileRequest,
      });
    },
  };
}
